/**
 * useSessionDurationMilestones
 *
 * Fires 30s / 2m / 10m engagement milestone events for an authenticated
 * session. Timers start on mount and clear on unmount, so MUST be
 * mounted at a stable node in the tree that does NOT remount on
 * route navigation. In this codebase that mount point is
 * <SessionMilestoneTracker /> rendered once at App scope (see App.tsx).
 *
 * DO NOT mount this hook inside <DashboardLayout>: DashboardLayout is
 * instantiated per-route in App.tsx, so the hook would unmount and
 * restart its timers on every navigation and the 10m milestone would
 * almost never fire.
 */

import { useEffect } from "react";
import { trackingService } from "../services/trackingService";
import { TrackingEvents } from "../services/trackingEvents";

const MILESTONES: Array<{ ms: number; event: (typeof TrackingEvents)[keyof typeof TrackingEvents] }> = [
  { ms: 30_000, event: TrackingEvents.ENGAGEMENT_SESSION_DURATION_30S },
  { ms: 120_000, event: TrackingEvents.ENGAGEMENT_SESSION_DURATION_2M },
  { ms: 600_000, event: TrackingEvents.ENGAGEMENT_SESSION_DURATION_10M },
];

/**
 * @param enabled - only start timers when true (e.g. authenticated).
 *   When flipped false, existing timers are cleared so we don't fire
 *   milestones for logged-out users.
 */
export const useSessionDurationMilestones = (enabled: boolean = true): void => {
  useEffect(() => {
    if (!enabled) return;

    const timers: ReturnType<typeof setTimeout>[] = MILESTONES.map(({ ms, event }) =>
      setTimeout(() => {
        trackingService.track(event, { milestone_ms: ms });
      }, ms)
    );

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [enabled]);
};

export default useSessionDurationMilestones;
